<template>
  <div class="budget-representantes">
    <p class="budget-explanation">O pedido usa as alocações ativas e vigentes da mesma categoria, na ordem abaixo. Ao esgotar uma, continua na próxima. ELT e GL não se misturam; cada débito fica registrado no budget de origem.</p>
    <p v-if="!representantes.length" class="budget-explanation">
      Nenhum representante vinculado ao seu usuário. Solicite ao administrador a revisão de Usuário × Representante.
    </p>
    <section v-for="rep in representantes" :key="rep.id" class="budget-representante">
      <h6>{{ rep.nome }} <small>({{ rep.id }})</small></h6>
      <p v-if="!rep.supervisores.length" class="budget-help">
        Nenhuma alocação de budget encontrada para este representante.
      </p>
      <p v-for="aviso in rep.avisos" :key="`aviso-${aviso.categoria}`" class="budget-help">
        {{ aviso.categoria }}: {{ mensagem(aviso, aviso.categoria) }}
      </p>
      <div v-for="rascunho in rep.rascunhos" :key="`rascunho-${rascunho.categoria}`" class="budget-draft">
        {{ rascunho.categoria }} em rascunhos (ainda não debitado): <strong>{{ moeda(rascunho.valor) }}</strong>
      </div>
      <div v-for="supervisor in rep.supervisores" :key="supervisor.id" class="budget-supervisor">
        <div class="budget-supervisor-name">Supervisor: {{ supervisor.nome }} <small>({{ supervisor.id }})</small></div>
        <div v-for="grupo in supervisor.categorias" :key="grupo.categoria" class="budget-category">
          <strong>{{ grupo.categoria }}</strong>
          <div v-for="item in grupo.itens" :key="chaveAlocacao(item)" class="budget-allocation">
            <div class="budget-description">{{ item.descricao || `Budget #${item.budgetid || 'não informado'}` }}</div>
            <div class="budget-period">
              {{ data(item.datainicio) || 'Início não informado' }} a {{ data(item.datatermino) || 'término não informado' }}
              · <span :class="`budget-status budget-status--${situacao(item)}`">{{ rotuloSituacao(item) }}</span>
            </div>
            <dl>
              <div><dt>Alocado</dt><dd>{{ moeda(item.valor) }}</dd></div>
              <div><dt>Utilizado</dt><dd>{{ moeda(item.valorgasto) }}</dd></div>
              <div class="budget-available"><dt>Disponível</dt><dd>{{ moeda(item.disponivel) }}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'BudgetPorRepresentante',
  props: { saldo: { type: Object, required: true } },
  computed: {
    representantes() {
      const grupos = new Map();
      (this.saldo.representantes || []).forEach(rep => {
        const id = String(rep.representanteid);
        grupos.set(id, {
          id, nome: rep.representante_nome || 'Representante', supervisoresMap: new Map(), avisos: [], rascunhos: [],
        });
      });
      (this.saldo.itens || []).forEach(item => {
        const id = String(item.representanteid);
        if (!grupos.has(id)) {
          grupos.set(id, {
            id, nome: item.representante_nome || 'Representante', supervisoresMap: new Map(), avisos: [], rascunhos: [],
          });
        }
        const rep = grupos.get(id);
        if (!item.temAlocacao) {
          if (item.categoria) rep.avisos.push(item);
          return;
        }
        const supervisorId = String(item.supervisorid == null ? 'nao-informado' : item.supervisorid);
        if (!rep.supervisoresMap.has(supervisorId)) {
          rep.supervisoresMap.set(supervisorId, {
            id: supervisorId,
            nome: item.supervisor_nome || 'Não informado',
            categoriasMap: new Map(),
          });
        }
        const supervisor = rep.supervisoresMap.get(supervisorId);
        const categoria = item.categoria || 'Não informada';
        if (!supervisor.categoriasMap.has(categoria)) supervisor.categoriasMap.set(categoria, []);
        supervisor.categoriasMap.get(categoria).push(item);
      });
      (this.saldo.rascunhos || []).forEach(item => {
        const rep = grupos.get(String(item.representanteid));
        if (rep && Number(item.valor) > 0) rep.rascunhos.push(item);
      });
      return [...grupos.values()].map(rep => ({
        ...rep,
        supervisores: [...rep.supervisoresMap.values()].map(supervisor => ({
          ...supervisor,
          categorias: [...supervisor.categoriasMap.entries()]
            .map(([categoria, itens]) => ({ categoria, itens }))
            .sort((a, b) => a.categoria.localeCompare(b.categoria)),
        })),
      })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },
  },
  methods: {
    moeda(valor) {
      return valor == null ? 'Não disponível' : Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    data(valor) {
      if (!valor) return '';
      return String(valor || '').slice(0, 10).split('-').reverse().join('/');
    },
    chaveAlocacao(item) {
      return item.budgetrepresentanteid || `${item.budgetid}-${item.categoria}-${item.datainicio}`;
    },
    situacao(item) {
      if (item.situacao) return item.situacao;
      const hoje = new Date().toISOString().slice(0, 10);
      const inicio = String(item.datainicio || '').slice(0, 10);
      const termino = String(item.datatermino || '').slice(0, 10);
      if (inicio && inicio > hoje) return 'futuro';
      if (termino && termino < hoje) return 'vencido';
      return inicio && termino ? 'vigente' : 'indisponivel';
    },
    rotuloSituacao(item) {
      return {
        vigente: 'Ativo',
        vencido: 'Expirado',
        futuro: 'Futuro',
        inativo: 'Inativo',
        indisponivel: 'Vigência não disponível',
      }[this.situacao(item)] || 'Vigência não disponível';
    },
    mensagem(item, categoria) {
      if (!item) return 'Não foi possível identificar o saldo desta categoria. Solicite a conferência ao administrador.';
      if (item.indisponivel) return `Não foi possível identificar o supervisor responsável pelo budget ${categoria} deste representante. O administrador precisa revisar essa responsabilidade. Até lá, o saldo não pode ser confirmado.`;
      const vigencia = item.budgetNaoVigente;
      if (vigencia && vigencia.situacao === 'vencido') return `O budget venceu em ${this.data(vigencia.datatermino)}. Solicite ao responsável um novo período de budget.`;
      if (vigencia && vigencia.situacao === 'futuro') return `O budget estará vigente a partir de ${this.data(vigencia.datainicio)}.`;
      if (item.motivo === 'SEM_SUPERVISOR') return 'Este representante não tem supervisor responsável pelo budget. Solicite ao administrador a revisão do vínculo.';
      if (item.motivo === 'SEM_BUDGET_VIGENTE') return 'Não há budget vigente nesta categoria. Solicite ao responsável a definição do budget.';
      return 'Nenhum valor foi alocado a este representante nesta categoria. Solicite a alocação ao responsável pelo budget.';
    },
  },
};
</script>

<style scoped>
.budget-representantes { width: 360px; max-width: calc(100vw - 32px); max-height: 65vh; overflow-y: auto; padding: 12px 16px; }
.budget-explanation, .budget-help { font-size: 12px; line-height: 1.5; margin: 0 0 10px; }
.budget-explanation { color: inherit; opacity: .8; }
.budget-representante + .budget-representante { margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(128,128,128,.3); }
h6 { margin: 0 0 10px; font-size: 14px; font-weight: 700; overflow-wrap: anywhere; }
h6 small { font-weight: 400; }
.budget-supervisor { margin-top: 12px; padding: 10px; border: 1px solid rgba(128,128,128,.3); border-radius: 5px; }
.budget-supervisor-name { font-size: 12px; font-weight: 700; overflow-wrap: anywhere; }
.budget-supervisor-name small { font-weight: 400; }
.budget-category { margin-top: 10px; }
.budget-category > strong { font-size: 12px; }
.budget-allocation { margin-top: 6px; padding-top: 6px; border-top: 1px dashed rgba(128,128,128,.25); }
.budget-description { font-size: 12px; font-weight: 600; overflow-wrap: anywhere; }
.budget-period, .budget-draft { font-size: 11px; line-height: 1.4; opacity: .85; }
.budget-draft { margin: 5px 0; }
.budget-status { font-weight: 700; }
.budget-status--vigente { color: #218838; }
.budget-status--vencido { color: #bd7000; }
.budget-status--futuro { color: #1665a8; }
.budget-status--indisponivel, .budget-status--inativo { color: #6c757d; }
.budget-help { margin-top: 4px; color: #bd7000; }
dl { margin: 4px 0 0; font-size: 12px; }
dl > div { display: flex; justify-content: space-between; gap: 12px; margin: 4px 0; }
dt { font-weight: 400; } dd { margin: 0; white-space: nowrap; }
.budget-available { font-weight: 700; } .budget-available dt { font-weight: 700; }
::v-deep .text-warning { color: inherit; }
</style>